import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiArrowLeftSLine,
  RiArrowRightSLine
} from "react-icons/ri";
import {
  GrServices
} from "react-icons/gr";
import {
  FaHotel,
  FaHouseChimneyUser
} from "react-icons/fa6";
import {
  MdApartment,
  MdRealEstateAgent
} from "react-icons/md";
import styles from './OptionBar.module.css';

const OptionBar = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const optionsContainerRef = useRef(null);
  const [showScrollIndicators, setShowScrollIndicators] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const options = [
    {
      to: '/rent',
      label: t('options.rent'),
      icon: <MdApartment className={styles.optionIcon} />,
      key: 'rent'
    },
    {
      to: '/buy',
      label: t('options.buy'),
      icon: <MdRealEstateAgent className={styles.optionIcon} />,
      key: 'buy'
    },
    {
      to: '/hotels',
      label: t('options.hotel'),
      icon: <FaHotel className={styles.optionIcon} />,
      key: 'hotel'
    },
    {
      to: '/guest-house', // Changed from '/' to '/guest-house'
      label: t('options.guest_house'),
      icon: <FaHouseChimneyUser className={styles.optionIcon} />,
      key: 'guest-house'
    },
    {
      to: '/serviceProvider',
      label: t('options.services'),
      icon: <GrServices className={styles.optionIcon} />,
      key: 'services'
    },
    {
      to: '/serviceProvider',
      label: t('options.vacation'),
      icon: <MdApartment className={styles.optionIcon} />,
      key: 'vacation'
    },
    {
      to: '/coliving',
      label: t('options.coliving'),
      icon: <FaHouseChimneyUser className={styles.optionIcon} />,
      key: 'coliving'
    },
    {
      to: '/land',
      label: t('options.land'),
      icon: <MdRealEstateAgent className={styles.optionIcon} />,
      key: 'land'
    }
  ];

  // Check scroll position and update indicators
  const updateScrollIndicators = () => {
    const container = optionsContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
      setShowScrollIndicators(scrollWidth > clientWidth);
    }
  };

  // Scroll functions
  const scrollLeft = () => {
    if (optionsContainerRef.current) {
      optionsContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (optionsContainerRef.current) {
      optionsContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  // Update scroll indicators on mount and resize
  useEffect(() => {
    updateScrollIndicators();
    window.addEventListener('resize', updateScrollIndicators);
    return () => window.removeEventListener('resize', updateScrollIndicators);
  }, []);

  // Update scroll indicators when options change
  useEffect(() => {
    const timer = setTimeout(updateScrollIndicators, 100);
    return () => clearTimeout(timer);
  }, [options]);

  return (
    <div className={styles.optionBar}>

      {/* Left Scroll Indicator */}
      {showScrollIndicators && canScrollLeft && (
        <button
          className={`${styles.scrollIndicator} ${styles.scrollLeft}`}
          onClick={scrollLeft}
          aria-label="Scroll left"
        >
          <RiArrowLeftSLine />
        </button>
      )}

      {/* Options Container */}
      <div
        className={styles.optionsContainer}
        ref={optionsContainerRef}
        onScroll={updateScrollIndicators}
      >
        <div className={styles.options}>
          {options.map((option) => (
            <Link
              key={option.key}
              className={styles.optionLink}
              to={option.to}
            >
              <div
                className={`${styles.option} ${location.pathname === option.to ? styles.active : ''
                  }`}
              >
                {option.icon}
                <span className={styles.optionLabel}>
                  {option.label}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Right Scroll Indicator */}
      {showScrollIndicators && canScrollRight && (
        <button
          className={`${styles.scrollIndicator} ${styles.scrollRight}`}
          onClick={scrollRight}
          aria-label="Scroll right"
        >
          <RiArrowRightSLine />
        </button>
      )}
    </div>
  );
};

export default OptionBar;